/* eslint-disable no-param-reassign */
import { DreamBlock } from '@typedream/core';
import { getNodeString } from '@udecode/plate-core';
import { isTextBlock } from '../../editorTools/queries';
import {
  createTPluginFactory,
  DreamPlatePlugin,
  ElementPluginOptions,
  getTElementPlugins,
} from '../../plate';
import { KEY_SLASH_COMMAND } from '../../pluginKeys';
import { getSelectedElementEntry } from '../elementSelection';
import { withSlashCommand } from './withSlashCommand';

export const createSlashCommandPlugin = createTPluginFactory({
  key: KEY_SLASH_COMMAND,
  withOverrides: withSlashCommand,
  handlers: {
    onKeyDown: (editor) => (event) => {
      if (!editor.selection) return undefined;

      const selectedElementEntry = getSelectedElementEntry(editor);

      if (!selectedElementEntry) return undefined;

      const [element] = selectedElementEntry;

      if (!isTextBlock(editor, element)) return undefined;

      if (event.key === 'Escape') {
        editor.store.setState({
          isSlashCommandOpen: false,
          slashCommandOptions: [],
        });
      } else if (
        ['ArrowDown', 'Down', 'ArrowUp', 'Up', 'Tab', 'Enter'].includes(
          event.key,
        )
        && editor.store.getState().isSlashCommandOpen
      ) {
        // prevent selection change,
        // the event will be handled by the SlashCommandMenu component
        event.preventDefault();
        return true;
      }

      return undefined;
    },

    onChange: (editor) => {
      const blocksWithSlashCommand = getTElementPlugins(editor).filter(
        (p) => p.isElement && p.options?.isDreamBlock,
      );

      const getSlashCommandOptions = (
        plugins: DreamPlatePlugin<ElementPluginOptions>[],
        key?: string,
      ) => plugins
        .filter((p) => {
          const pattern = new RegExp(key || '', 'i');
          return (
            pattern.test(p.options?.meta?.title || '')
              || p.options?.meta?.keywords?.some?.((k) => pattern.test(k))
              || pattern.test(p.key)
          );
        })
        .map((p) => p.type) as DreamBlock['type'][];

      /* Handle slash command options filtering */
      return () => {
        if (!editor.selection) return;

        const selectedElementEntry = getSelectedElementEntry(editor);

        if (!selectedElementEntry) return;

        const [element] = selectedElementEntry;

        if (!getNodeString(element).startsWith('/')) {
          // close when '/' is deleted and slash command is still open
          if (editor.store.getState().isSlashCommandOpen) {
            editor.store.setState({
              isSlashCommandOpen: false,
              slashCommandOptions: [],
            });
          }

          // nothing else needs to be done since slash command is not open
          // and node text is not starting with /
          return;
        }

        const options = getSlashCommandOptions(
          blocksWithSlashCommand,
          getNodeString(element).replace(/^\//, ''),
        );

        // when filter result is empty and it's already empty before, close slash command
        // otherwise just update the options
        editor.store.setState({
          isSlashCommandOpen: !(
            options.length === 0
            && editor.store.getState().slashCommandOptions.length === 0
          ),
          slashCommandOptions: options,
        });
      };
    },
  },
});
